import type { ColorDeclaration, FontName, Strata, ElementState, CssSpaceValue, } from "./semantics";
import * as library from "@bracket/ui/themes/library";
import type { SnakeCaseObjectKeys, } from "@bracket/sharedTypes";
import type { CSSProperties, } from "react";

export declare type ThemeName = SnakeCaseObjectKeys<typeof library> & string;

export const THEME_NAMES: ThemeName[] = Object.keys(library).map(key => key as ThemeName);

export const isThemeName = ( name: string ): name is ThemeName => {
	return typeof name === "string" && THEME_NAMES.includes(name as ThemeName);
};


export declare type SwatchVariant = "neutral" | "success" | "warning" | "error" | "accent_blue" | "accent_teal" | "accent_orange" | "accent_pink" | "accent_purple" | "accent_brand" | "primary" | "surface" | "surface_on";
export declare type SwatchList = keyof Pick<ThemePalette, "swatch_one">;
export declare type TypeVariant = "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
export declare type SizeVariant = "xxsm" | "xsm" | "sm" | "md" | "lg" | "xl" | "xxl";
export declare type HeadingVariant = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
export declare type EmphasisVariant = "slightest" | "slighter" | "slight" | "bold" | "bolder" | "boldest";
export declare type StrokeVariant = `stroke_${EmphasisVariant}` | `stroke`;
export declare type SelectableStrokeVariant = "normal" | EmphasisVariant | ColorDeclaration;
export declare type CSSGradientDeclaration = string;
export declare type CSSShadowDeclaration = string;
export declare type AllowedImageExtension = "png"|"svg"|"jpg"|"jpeg";
export declare type RelativeImageUrl = `/${string}.${AllowedImageExtension}`;
export declare type AbsoluteImageUrl = `https://${string}.${AllowedImageExtension}`;
export declare type NamedStylingItem = "primary" | "secondary" | "tertiary" | "quaternary" | "quinary" | "senary" | "navpanel";

export declare type PixelCount = number;


export declare type ColorPalette = {
	palette_type: "light" | "dark";
	white: ColorDeclaration;
	black: ColorDeclaration;
	background: ColorDeclaration;
	background_on: ColorDeclaration;
	title: ColorDeclaration;
	heading: ColorDeclaration;
	subheading: ColorDeclaration;
	paragraph: ColorDeclaration;
	primary: ColorDeclaration;
	primary_variant: ColorDeclaration;
	primary_on: ColorDeclaration;
	surface: ColorDeclaration;
	surface_on: ColorDeclaration;
	surface_container: ColorDeclaration;
	surface_container_on: ColorDeclaration;
}

export declare type ThemePalette = ColorPalette & {

	primary_container: ColorDeclaration;
	primary_container_on: ColorDeclaration;
	primary_fixed: ColorDeclaration;
	primary_fixed_dim: ColorDeclaration;
	outline: ColorDeclaration;
	outline_variant: ColorDeclaration;

	surface_light: ColorDeclaration;
	surface_light_on: ColorDeclaration;

	surface_dark: ColorDeclaration;
	surface_dark_on: ColorDeclaration;

	surface_hover: ColorDeclaration;
	surface_variant: ColorDeclaration;
	surface_variant_on: ColorDeclaration;
	surface_inverse: ColorDeclaration;
	surface_inverse_on: ColorDeclaration;
	surface_container_lowest: ColorDeclaration;
	surface_container_low: ColorDeclaration;
	surface_container_high: ColorDeclaration;
	surface_container_highest: ColorDeclaration;
	surface_container_bright: ColorDeclaration;
	surface_container_dim: ColorDeclaration;

	success_container: ColorDeclaration;
	success: ColorDeclaration;
	success_on: ColorDeclaration;

	error_container: ColorDeclaration;
	error: ColorDeclaration;
	error_on: ColorDeclaration;

	warning: ColorDeclaration;
	warning_on: ColorDeclaration;
	warning_container: ColorDeclaration;

	debug: ColorDeclaration;
	debug_on: ColorDeclaration;

	accent_blue: ColorDeclaration;
	accent_teal: ColorDeclaration;
	accent_orange: ColorDeclaration;
	accent_pink: ColorDeclaration;
	accent_purple: ColorDeclaration;
	accent_brand: ColorDeclaration;

	swatches: SwatchList;
	swatch_one: [ColorDeclaration, ColorDeclaration, ColorDeclaration, ColorDeclaration, ColorDeclaration];

	tint_lightest: ColorDeclaration;
	tint_lighter: ColorDeclaration;
	tint_light: ColorDeclaration;
	tine: ColorDeclaration;
	tint_dark: ColorDeclaration;
	tint_darker: ColorDeclaration;
	tint_darkest: ColorDeclaration;

}


export declare type Theme = ThemePalette & {
	name: ThemeName;
	palette_type: "light" | "dark";
	fonts: FontPalette;
	typeface: {
		p: ThemeTypePalette;
	} & ThemeHeadingPalette;
	chart: ThemeChartPalette;
	assets: AssetPalette;
	gradients: GradientPalette;
	shadows: ShadowPalette;
	radials: RadialPalette;
	strata: ( level?: Strata ) => ColorDeclaration;
	unique: ( key: string ) => CSSProperties;
}


export declare type ThemeTypePalette = {
	[key in SizeVariant]: {
		font: string;
		size: string;
		lineHeight: string;
		weight: string;
		color: ColorDeclaration;
	}
}


export declare type ThemeHeadingPalette = {
	[key in HeadingVariant]: {
		font: string;
		size: string;
		lineHeight: string;
		weight: string;
		color: ColorDeclaration;
	}
}


export declare type ThemeChartPalette = {
	stroke: ColorDeclaration;
	stroke_width: PixelCount;
	axis: {
		color: ColorDeclaration;
		swatch: SwatchList;
	}
}


export declare type FontPalette = {
	numeric: string;
	body: string;
	heading: string;
	heading_variant: string;
};


export declare type GradientPalette = {
	[key in NamedStylingItem]: CSSGradientDeclaration;
}

export declare type ShadowPalette = {
	[key in NamedStylingItem]: CSSShadowDeclaration;
}

export declare type AssetPaletteName = "HERO_BG" | "menu_background" | "invest_background" | "background_one" | "TX_BKG_ONE" | "TX_GRAIN_ONE";
export declare type AssetPalette = {
	[key in AssetPaletteName]: AbsoluteImageUrl | RelativeImageUrl;
}

export declare type ThemeStorageKey = "bracket-ui-theme";
export declare type ThemeStorageMap = {
	[key in ThemeStorageKey]: ThemeName;
}

export declare type SizePalette<T extends string> = Record<SizeVariant, T>;

export declare type RadialPalette = SizePalette<CssSpaceValue>;

export const THEME_STORAGE_KEYS: ThemeStorageKey[] = ["bracket-ui-theme"];


/** Typeguard - Determines if the provided key is a valid ThemeStorageKey.
 * @param key - The string to check for theme storage key validity
 * @returns True if the key is a supported ThemeStorageKey, false otherwise */
export const isThemeStorageKey = ( key: string ): key is ThemeStorageKey =>
	typeof key === "string" && THEME_STORAGE_KEYS.includes(key as ThemeStorageKey);

